import { ForwardedRef, forwardRef, useMemo } from "react";
import Select, { SelectInstance } from "react-select";
import { StateManagerProps } from "react-select/dist/declarations/src/useStateManager";
import { keyBy } from "lodash";
import { FilterVariantDefinition } from "components/Shared/FilterEditor/utils";
import useSimpleValue, {
  SimpleValueProps,
} from "components/Shared/Select/useSimpleValue";
import useSelectStyles from "utils/useSelectStyles";

type Option = FilterVariantDefinition;

function getOptionValue(option: Option) {
  return option.id;
}

type Props<IsMulti extends boolean = false> = StateManagerProps<
  Option,
  IsMulti
> &
  SimpleValueProps<string, IsMulti> & {
    variants: FilterVariantDefinition[] | undefined;
  };

function VariantSelect<IsMulti extends boolean = false>(
  {
    isMulti,
    variants = [],
    styles,
    value: rawValue,
    onChange,
    ...rest
  }: Props<IsMulti>,
  ref: ForwardedRef<SelectInstance<Option, IsMulti>>,
) {
  const styleProps = useSelectStyles(styles);

  const variantsById = useMemo(() => keyBy(variants, "id"), [variants]);

  const { value, handleChange } = useSimpleValue({
    getOptionFromValue: (filterDefinitionId) =>
      variantsById[filterDefinitionId],
    getValueFromOption: (option) => option.id,
    isMulti,
    onChange,
    value: rawValue,
  });

  return (
    <Select
      {...styleProps}
      {...{ ref, getOptionValue, value }}
      options={variants}
      maxMenuHeight={800}
      onChange={handleChange}
      placeholder="Select variant…"
      menuPortalTarget={document.body}
      {...rest}
    />
  );
}

export default forwardRef(VariantSelect);
